import React, { useState, useEffect } from 'react';
import { X, Activity, User, Clock, Edit, Trash2, Play, Pause, CheckCircle, MessageSquare, FileText, Calendar, ArrowRight } from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface ActivityLogProps {
  isOpen: boolean;
  onClose: () => void;
  taskId?: string | null;
  showAllTasks?: boolean;
}

interface ActivityEntry {
  id: string;
  taskId: string;
  taskTitle: string;
  action: 'created' | 'updated' | 'status_changed' | 'assigned' | 'time_added' | 'comment_added' | 'deleted' | 'completed';
  description: string;
  userId: string;
  userName: string;
  timestamp: string;
  metadata?: {
    oldValue?: string;
    newValue?: string;
    timeMinutes?: number;
    comment?: string;
    assigneeName?: string;
    fieldChanged?: string;
  };
}

export const ActivityLog: React.FC<ActivityLogProps> = ({
  isOpen,
  onClose,
  taskId,
  showAllTasks = false
}) => {
  const [activities, setActivities] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'status_changes' | 'assignments' | 'reallocations' | 'time_entries' | 'comments'>('all');

  useEffect(() => {
    if (isOpen) {
      fetchActivities();
    }
  }, [isOpen, taskId, showAllTasks]);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const endpoint = showAllTasks 
        ? '/firm-admin/tasks/activity-log'
        : `/firm-admin/tasks/${taskId}/activity-log`;
      
      const response = await api.get(endpoint);
      if (response.data.success) {
        setActivities(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching activity log:', error);
      // Mock data for demonstration
      setActivities([
        {
          id: '1',
          taskId: 'task-1',
          taskTitle: 'Q4 Financial Statements - ABC Manufacturing',
          action: 'created',
          description: 'Task created',
          userId: 'user-1',
          userName: 'John Smith',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        },
        {
          id: '2',
          taskId: 'task-1',
          taskTitle: 'Q4 Financial Statements - ABC Manufacturing',
          action: 'assigned',
          description: 'Task assigned to Sarah Johnson as preparer',
          userId: 'user-1',
          userName: 'John Smith',
          timestamp: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(),
          metadata: {
            assigneeName: 'Sarah Johnson'
          }
        },
        {
          id: '3',
          taskId: 'task-1',
          taskTitle: 'Q4 Financial Statements - ABC Manufacturing',
          action: 'status_changed',
          description: 'Status changed from Draft to In Progress',
          userId: 'user-2',
          userName: 'Sarah Johnson',
          timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
          metadata: {
            oldValue: 'DRAFT',
            newValue: 'IN_PROGRESS'
          }
        },
        {
          id: '4',
          taskId: 'task-1',
          taskTitle: 'Q4 Financial Statements - ABC Manufacturing',
          action: 'time_added',
          description: 'Added 2 hours of work time',
          userId: 'user-2',
          userName: 'Sarah Johnson',
          timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
          metadata: {
            timeMinutes: 120
          }
        },
        {
          id: '5',
          taskId: 'task-2',
          taskTitle: 'Tax Return Review - Tech Solutions Inc.',
          action: 'comment_added',
          description: 'Added comment: "Need additional documentation from client"',
          userId: 'user-3',
          userName: 'Mike Davis',
          timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
          metadata: {
            comment: 'Need additional documentation from client'
          }
        },
        {
          id: '6',
          taskId: 'task-3',
          taskTitle: 'Monthly Bookkeeping - Green Energy Corp.',
          action: 'completed',
          description: 'Task marked as completed',
          userId: 'user-4',
          userName: 'Lisa Wilson',
          timestamp: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (action: string) => {
    switch (action) {
      case 'created':
        return <FileText className="w-4 h-4 text-blue-500" />;
      case 'updated':
        return <Edit className="w-4 h-4 text-yellow-500" />;
      case 'status_changed':
        return <Activity className="w-4 h-4 text-purple-500" />;
      case 'assigned':
        return <User className="w-4 h-4 text-green-500" />;
      case 'task_reallocated':
        return <ArrowRight className="w-4 h-4 text-blue-500" />;
      case 'returned_to_preparer':
        return <ArrowRight className="w-4 h-4 text-orange-500" />;
      case 'returned_to_reviewer':
        return <ArrowRight className="w-4 h-4 text-orange-500" />;
      case 'time_added':
        return <Clock className="w-4 h-4 text-orange-500" />;
      case 'comment_added':
        return <MessageSquare className="w-4 h-4 text-indigo-500" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'deleted':
        return <Trash2 className="w-4 h-4 text-red-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const filteredActivities = activities.filter(activity => {
    if (filter === 'all') return true;
    if (filter === 'status_changes') return activity.action === 'status_changed';
    if (filter === 'assignments') return activity.action === 'assigned';
    if (filter === 'reallocations') return activity.action === 'task_reallocated';
    if (filter === 'time_entries') return activity.action === 'time_added';
    if (filter === 'comments') return activity.action === 'comment_added';
    return true;
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Activity className="w-6 h-6 text-gray-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {showAllTasks ? 'All Task Activity' : 'Task Activity Log'}
              </h2>
              <p className="text-sm text-gray-600">
                {showAllTasks ? 'Recent activity across all tasks' : 'Activity history for this task'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {[
            { key: 'all', label: 'All Activity' },
            { key: 'status_changes', label: 'Status Changes' },
            { key: 'assignments', label: 'Assignments' },
            { key: 'reallocations', label: 'Reallocations' },
            { key: 'time_entries', label: 'Time Entries' },
            { key: 'comments', label: 'Comments' }
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key as any)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                filter === key
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Activity List */}
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            </div>
          ) : filteredActivities.length > 0 ? (
            <div className="p-6">
              <div className="space-y-4">
                {filteredActivities.map((activity, index) => (
                  <div key={activity.id} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        {getActivityIcon(activity.action)}
                      </div>
                      {index < filteredActivities.length - 1 && (
                        <div className="w-px h-6 bg-gray-200 mx-auto mt-2"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {activity.description}
                          </p>
                          {showAllTasks && (
                            <p className="text-xs text-gray-500 mt-1">
                              Task: {activity.taskTitle}
                            </p>
                          )}
                          {activity.metadata && (
                            <div className="mt-2 text-xs text-gray-600">
                              {activity.metadata.comment && (
                                <div className="bg-gray-50 p-2 rounded border-l-2 border-indigo-200">
                                  "{activity.metadata.comment}"
                                </div>
                              )}
                              {activity.metadata.reason && (
                                <div className="bg-orange-50 p-2 rounded border-l-2 border-orange-200 mt-1">
                                  <span className="font-medium text-orange-800">Rework Reason:</span>
                                  <span className="text-orange-700 ml-1">"{activity.metadata.reason}"</span>
                                </div>
                              )}
                              {activity.metadata.timeMinutes && (
                                <span className="inline-flex items-center px-2 py-1 bg-orange-100 text-orange-800 rounded-full">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {formatTime(activity.metadata.timeMinutes)}
                                </span>
                              )}
                              {activity.metadata.oldValue && activity.metadata.newValue && (
                                <div className="flex items-center gap-2">
                                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">
                                    {activity.metadata.oldValue.replace('_', ' ')}
                                  </span>
                                  <span className="text-gray-400">→</span>
                                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                    {activity.metadata.newValue.replace('_', ' ')}
                                  </span>
                                </div>
                              )}
                              {activity.metadata.assigneeName && (
                                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                  <User className="w-3 h-3 mr-1" />
                                  {activity.metadata.assigneeName}
                                </span>
                              )}
                            </div>
                          )}
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-xs text-gray-500">
                              {formatTimeAgo(activity.timestamp)}
                            </span>
                            <span className="text-xs text-gray-500">
                              by {activity.userName}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No activity found</p>
              <p className="text-sm text-gray-400 mt-1">
                Activity will appear here as tasks are created and updated
              </p>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500 text-center">
            Activity is tracked automatically and shows the most recent 50 entries
          </p>
        </div>
      </div>
    </div>
  );
};
